import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import MicRecorder from 'mic-recorder-to-mp3';

import { useAppSelector } from 'src/redux/store';
import { EVENTS, SCREENS } from 'src/analytics/events';
import { trackEvent } from 'src/analytics';
import { setLoading, setTextQuery } from 'src/redux/slices/open_ai';
import Loader from 'src/components/atoms/loader';

import sendBtn from 'src/assets/images/send_btn.png'
import recordIcon from 'src/assets/images/record_icon.svg';
import recordStopIcon from 'src/assets/images/record_stop_icon.svg';
import LandingIcon from 'src/assets/images/landing_page.gif';

import styles from './index.module.scss';
import SECRETS from 'src/common/secrets';

interface QueryInputProps {
    onPressStartStream: (text: string | undefined | null) => void;
    disabled?: boolean;
};

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const QueryInput: React.FunctionComponent<QueryInputProps> = (props) => {
    const dispatch = useDispatch();
    const textareaRef = useRef<HTMLInputElement>(null);

    const { loading, textQuery, threadFocus, openAIResponse } = useAppSelector(state => state.open_ai);
    const { socketConnection, pdfSocketConnection } = useAppSelector(state => state.socket);

    const { onPressStartStream, disabled = false } = props ?? {};

    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [voiceLoading, setVoiceLoading] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    useEffect(() => {
        if (textareaRef.current && threadFocus) {
            // Delay to ensure WebView handles focus properly
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    // Force cursor to end of input
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 100);
        }
        dispatch(setLoading(false));
    }, [textareaRef, threadFocus, dispatch]);

    const handleTextareaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setTextQuery(event.target.value));
        
        // Force re-render on iOS WebView
        if (textareaRef.current) {
            textareaRef.current.style.opacity = "0.99";
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.style.opacity = "1";
                }
            }, 10);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        tapOnChat();
        
        // Delay to ensure WebView handles focus properly
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                // Force cursor to end of input
                const length = textareaRef.current.value.length;
                textareaRef.current.setSelectionRange(length, length);
            }
        }, 100);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const tapOnChat = () => {
        trackEvent(EVENTS.LANDING_SCREEN.TAP_ON_CHAT, {
            screen_name: SCREENS.LANDING_PAGE,
            utm: true,
            search: window.location.search
        });
    };

    const tapOnSend = () => {
        if (textQuery && (socketConnection || pdfSocketConnection)) {
            trackEvent(EVENTS.LANDING_SCREEN.MESSAGE_SENT, {
                query_sent: textQuery,
                screen_name: SCREENS.LANDING_PAGE,
                utm: true,
                search: window.location.search
            });
        }
        onPressStartStream('');
    };

    const startRecording = async () => {
        try {
            await Mp3Recorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error(error);
        }
    };

    const stopRecording = async () => {
        try {
            setVoiceLoading(true);
            const [_buffer, blob] = await Mp3Recorder.stop().getMp3();
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = async () => {
                setVoiceLoading(true);
                if (reader?.result && typeof reader?.result === 'string') {
                    const base64String = reader.result.split(',')[1];

                    const ASRHeader = new Headers();
                    ASRHeader.append('Authorization', process.env.REACT_APP_BHASHINI_APIKEY || SECRETS.REACT_APP_BHASHINI_APIKEY);
                    ASRHeader.append('Content-Type', 'application/json');

                    const ASRPayload = {
                        pipelineTasks: [
                            {
                                taskType: 'asr',
                                config: {
                                    language: { sourceLanguage: 'en' },
                                    serviceId: 'ai4bharat/whisper-medium-en--gpu--t4',
                                    audioFormat: 'flac',
                                    samplingRate: 16000,
                                    preProcessors: ['vad'],
                                    // postProcessors: ['itn']
                                }
                            }
                        ],
                        inputData: {
                            audio: [{ audioContent: base64String }]
                        }
                    };

                    const ASROptions = {
                        method: 'POST',
                        headers: ASRHeader,
                        body: JSON.stringify(ASRPayload),
                    };

                    const response = await fetch('https://dhruva-api.bhashini.gov.in/services/inference/pipeline', ASROptions);
                    const result = await response.json();
                    dispatch(setTextQuery(result?.pipelineResponse[0]?.output[0]?.source || ''));
                    // onPressStartStream(result?.pipelineResponse[0]?.output[0]?.source || '');
                    setVoiceLoading(false);
                }
            };
        } catch (error) {
            console.error(error);
        } finally {
            setIsRecording(false);
            setVoiceLoading(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, functionToBeCalled: Function) => {
        if (event.key === 'Enter') {
            functionToBeCalled();
        }
    };

    const getInputStyle = () => {
        const baseStyle = disabled ? { cursor: 'no-drop' } : {};
        
        // iOS WebView specific fixes
        return {
            ...baseStyle,
            // iOS WebView specific fixes
            WebkitAppearance: "none",
            WebkitUserSelect: "text",
            userSelect: "text",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            willChange: "contents",
            // Force re-render when focused to show cursor
            opacity: isFocused ? 0.99 : 1,
        };
    };

    return (
        <div className={styles.text_area_wrapper}>
            <div className={styles.input_container}>
                <input
                    placeholder={openAIResponse.length ? openAIResponse[openAIResponse.length - 1].status === 'error' ? 'Ask anything' : 'Ask follow-up' : 'Ask anything'}
                    className={styles.chat_textarea}
                    onChange={handleTextareaInput}
                    value={textQuery as string}
                    ref={textareaRef}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={disabled}
                    style={getInputStyle() as React.CSSProperties}
                    onKeyDown={(event) => handleKeyDown(event, tapOnSend)}
                    // iOS specific attributes
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                />
                {voiceLoading &&
                    <div className={styles.loader_icon}>
                        <Loader noBackground />
                    </div>}
                {<>
                    {!voiceLoading &&
                        <>{!isRecording
                            ? <img src={recordIcon} className={styles.record_icon} onClick={startRecording} alt='record' />
                            : <img src={recordStopIcon} className={styles.record_stop_icon} onClick={stopRecording} alt='recordStop' />
                        }</>
                    }
                </>}
            </div>
            {(openAIResponse.some(obj => obj.loader === true) || loading)
                ? <img src={LandingIcon} className={styles.loader_icon} alt='loader' />
                : <img src={sendBtn} className={styles.send_btn} alt='send' onClick={tapOnSend} style={disabled ? { cursor: 'no-drop' } : {}} />}
        </div>
    );
}

export default QueryInput;